import React from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { GrnAuditEvent } from '@/types/domain/grn-management';
import { formatDate } from '@/lib/utils';
import { History, Database, UserCheck, AlertOctagon, CheckCircle2, ShieldCheck, Clock } from 'lucide-react';

interface GrnAuditHistoryTabProps {
  auditHistory: GrnAuditEvent[];
}

export const GrnAuditHistoryTab: React.FC<GrnAuditHistoryTabProps> = ({ auditHistory }) => {
  const getEventBadge = (eventType: GrnAuditEvent['eventType']) => {
    switch (eventType) {
      case 'SAP_SYNC_SUCCESS':
      case 'ITEMS_ACCEPTED':
        return (
          <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/20 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> {eventType}
          </span>
        );
      case 'ITEMS_REJECTED':
      case 'QUALITY_HOLD_APPLIED':
      case 'SAP_SYNC_FAILED':
        return (
          <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-700 border border-rose-500/20 flex items-center gap-1">
            <AlertOctagon className="h-3 w-3" /> {eventType}
          </span>
        );
      case 'INSPECTION_RECORDED':
        return (
          <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-700 border border-blue-500/20 flex items-center gap-1">
            <ShieldCheck className="h-3 w-3" /> {eventType}
          </span>
        );
      default:
        return (
          <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-700 border border-purple-500/20 flex items-center gap-1">
            <Clock className="h-3 w-3" /> {eventType}
          </span>
        );
    }
  };

  const getSourceIcon = (source: GrnAuditEvent['systemSource']) => {
    switch (source) {
      case 'SAP_BAPI':
        return <Database className="h-3.5 w-3.5 text-purple-600" />;
      case 'System':
        return <AlertOctagon className="h-3.5 w-3.5 text-slate-500" />;
      default:
        return <UserCheck className="h-3.5 w-3.5 text-primary" />;
    }
  };

  return (
    <div className="space-y-4 font-heading">
      <div className="flex items-center justify-between pb-2 border-b border-border/60">
        <div>
          <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
            <History className="h-4 w-4 text-primary" /> SOX-Compliant Goods Receipt Audit Log
          </h3>
          <p className="text-xs text-foreground-secondary">
            Immutable transaction history, quality inspection logs, and SAP MIGO posting records.
          </p>
        </div>
        <span className="text-[11px] font-mono font-bold text-foreground-muted bg-surface px-2 py-0.5 rounded border border-border">
          {auditHistory.length} Events Logged
        </span>
      </div>

      <div className="relative pl-6 space-y-4 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-border">
        {auditHistory.map((item, idx) => (
          <Card key={item.id || idx} className="rounded-xl border-border bg-card shadow-2xs relative">
            {/* Timeline node */}
            <div className="absolute -left-[27px] top-4 h-3.5 w-3.5 rounded-full border-2 border-primary bg-card" />

            <CardContent className="p-4 space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/40 pb-2">
                <div className="flex flex-wrap items-center gap-2">
                  {getEventBadge(item.eventType)}
                  <span className="text-xs font-extrabold text-foreground">{item.actorName}</span>
                  <span className="text-[10px] text-foreground-secondary font-medium">({item.actorRole})</span>
                </div>

                <div className="flex items-center gap-2 text-xs text-foreground-muted font-mono">
                  <span className="flex items-center gap-1">
                    {getSourceIcon(item.systemSource)} {item.systemSource}
                  </span>
                  <span>•</span>
                  <span>{formatDate(item.timestamp)}</span>
                </div>
              </div>

              <p className="text-xs text-foreground font-medium leading-relaxed bg-surface/40 p-2.5 rounded-lg border border-border/30">
                {item.details}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
